import {
  ArrowRight,
  Bookmark,
  BookOpen,
  CheckCircle2,
  Headphones,
  History,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KNOWLEDGE_AUDIO_AUTOPREWARM_ENABLED } from '../../api/config';
import { PageHeader } from '../../components/PageHeader';
import { showToast } from '../../components/Toast';
import {
  ContextAskButton,
  type AeternaLoopContextValue,
} from '../../components/aeterna-loop';
import { chatApi } from '../../features/chat/api';
import {
  executeApi,
  type LongevityAudio,
  type LongevityPlanDaysResponse,
} from '../../features/execute/api';
import {
  getPlanGoalCards,
  type LongevityPlan,
} from '../../features/execute/longevity';
import {
  knowledgeApi,
  type PersonalizedKnowledgeQueueItem,
  type PersonalizedKnowledgeQueueResponse,
  type PodcastEpisodeWithProgress,
  type PodcastSeries,
} from '../../features/knowledge/api';
import {
  getResolvedCustomCourseTitle,
  type CustomCourseOutlineContext,
} from '../knowledge/customCourseOutline';
import {
  getCachedCustomCourseLesson,
  getCachedCustomCourseHub,
  getCachedPodcastSeries,
  setCachedCustomCourseLesson,
  setCachedCustomCourseHub,
  setCachedPodcastSeries,
  type CustomCourseHubSnapshot,
} from '../knowledge/customCourseSessionCache';
import styles from '../knowledge/KnowledgePodcastPages.module.css';

let homeSnapshotRequest: Promise<CustomCourseHubSnapshot> | null = null;
let podcastSeriesRequest: Promise<PodcastSeries[]> | null = null;
const HOME_SNAPSHOT_TIMEOUT_MS = 8_000;
const SERIES_TIMEOUT_MS = 6_000;
const PLAYBACK_RATES = [1, 1.25, 1.5] as const;
const AUDIO_POLL_INTERVAL_MS = 5_000;
const AUDIO_PREWARM_START_DELAY_MS = 1_200;
const AUDIO_PREWARM_SPACING_MS = 1_200;
const PERSONALIZED_PULSE_CARD_LIMIT = 3;
const GENERAL_PULSE_CARD_LIMIT = 6;

type KnowledgeCardFeedback = 'helpful' | 'dismissed' | 'saved';

class KnowledgeHomeTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'KnowledgeHomeTimeoutError';
  }
}

function withKnowledgeTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<T> {
  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(
      () => reject(new KnowledgeHomeTimeoutError(timeoutMessage)),
      timeoutMs,
    );
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  });
}

function getLoadErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function loadHomeSnapshot() {
  if (!homeSnapshotRequest) {
    homeSnapshotRequest = (async () => {
      const [queue, activePlan] = await Promise.all([
        knowledgeApi.getPersonalizedQueue().catch(() => null),
        executeApi.getActiveLongevityPlan(),
      ]);
      if (!activePlan) return { plan: null, days: [], queue };
      const response = await executeApi.getLongevityDays(activePlan.id);
      return { plan: activePlan, days: response.days, queue };
    })().finally(() => {
      homeSnapshotRequest = null;
    });
  }
  return homeSnapshotRequest;
}

function loadPodcastSeries() {
  if (!podcastSeriesRequest) {
    podcastSeriesRequest = knowledgeApi.getPodcastSeries().finally(() => {
      podcastSeriesRequest = null;
    });
  }
  return podcastSeriesRequest;
}

function getOutlineContext(
  day: LongevityPlanDaysResponse['days'][number],
  goals: ReturnType<typeof getPlanGoalCards>,
): CustomCourseOutlineContext {
  const goal = goals.find((item) => item.id === day.primaryGoalCardId);
  return {
    goalTitle: goal?.title,
    taskTitle: day.taskTitle,
    abilityTags: goal?.abilityTags,
  };
}

function getLessonTitle(
  day: LongevityPlanDaysResponse['days'][number],
  goals: ReturnType<typeof getPlanGoalCards>,
) {
  return getResolvedCustomCourseTitle(
    day.dayNumber,
    day.title,
    getOutlineContext(day, goals),
  );
}

function getAudienceLevelLabel(level: PodcastSeries['audienceLevel']) {
  if (level === 'deep_dive') return '深潜';
  if (level === 'standard') return '标准';
  return '入门';
}

function getGeneralPulseTitle(
  series: PodcastSeries,
  episode: PodcastEpisodeWithProgress | null | undefined,
) {
  return episode?.title ?? series.title;
}

function getGeneralPulseSummary(
  series: PodcastSeries,
  episode: PodcastEpisodeWithProgress | null | undefined,
) {
  return episode?.summary ?? series.summary;
}

function getGeneralPulsePath(
  series: PodcastSeries,
  episode: PodcastEpisodeWithProgress | null | undefined,
) {
  return episode
    ? `/knowledge/podcasts/${series.id}/episodes/${episode.id}`
    : `/knowledge/podcasts/${series.id}`;
}

function getGeneralPulseContext(
  series: PodcastSeries,
  episode: PodcastEpisodeWithProgress | null | undefined,
): AeternaLoopContextValue {
  const title = getGeneralPulseTitle(series, episode);
  return {
    sourceType: 'course',
    sourceId: episode?.id ?? series.id,
    sourceTitle: title,
    stage: 'understand',
    evidenceBoundary:
      '通识课提供背景知识，不读取你的个人数值，也不替代个性化判断或医生诊断。',
    askPrompt: episode
      ? `我正在看通识单集《${title}》，它属于《${series.title}》。请先说明它能补充哪类背景知识；如果要结合我的个人资料，请先说明缺口。`
      : `我正在看通识课《${series.title}》。请先说明它和我的当前行动或健康问题可能有什么关系；如果缺少个人资料，请直接说明缺口。`,
  };
}

function getFeedbackToast(type: KnowledgeCardFeedback, learningEnabled: boolean) {
  if (!learningEnabled) return '已在本页记录；AI 学习已关闭';
  if (type === 'saved') return '已记住你想保留这类内容';
  if (type === 'dismissed') return '已减少这类内容的优先级';
  return '已记住这类内容对你有帮助';
}

function buildTopicSuggestions(
  currentLesson: LongevityPlanDaysResponse['days'][number] | undefined,
  goals: ReturnType<typeof getPlanGoalCards>,
) {
  const primaryGoal = goals[0]?.title ?? '百岁行动能力';
  const taskTitle = currentLesson?.taskTitle ?? '当前小行动';
  return [
    {
      title: `为什么先做“${taskTitle}”`,
      reason: '个性化真知',
    },
    {
      title: `这个目标要练哪些能力`,
      reason: primaryGoal,
    },
    {
      title: '哪些结论不能直接套用到我身上',
      reason: '安全边界',
    },
  ];
}

interface KnowledgeQueueSuggestion {
  id: string;
  title: string;
  reason: string;
  summary?: string;
  readiness?: string;
  imageRef?: Record<string, unknown>;
  imageAltText?: string;
  visualReviewStatus?: string;
}

function buildQueueSuggestions(
  items: PersonalizedKnowledgeQueueItem[],
  fallback: ReturnType<typeof buildTopicSuggestions>,
): KnowledgeQueueSuggestion[] {
  if (items.length === 0) {
    return fallback.map((item, index) => ({
      ...item,
      id: `fallback-${index}`,
    }));
  }
  return items.map((item) => ({
    id: item.id,
    title: item.title,
    reason: item.direction,
    summary: item.summary,
    readiness: item.readiness,
    imageRef: item.imageRef,
    imageAltText: item.imageAltText,
    visualReviewStatus: item.visualReviewStatus,
  }));
}

function queueLabelForIndex(index: number) {
  return String.fromCharCode(65 + index);
}

function getReadinessLabel(readiness: string | undefined) {
  if (readiness === 'playable') return '可听';
  if (readiness === 'audio_processing') return '音频准备中';
  return '文字可读';
}

function readinessFromAudioStatus(status: string | undefined) {
  if (status === 'completed') return 'playable';
  if (status === 'processing') return 'audio_processing';
  return 'text_ready';
}

function shouldPrewarmAudio(status: string | undefined) {
  return !status || status === 'pending' || status === 'degraded';
}

function isAudioTerminal(status: string | undefined) {
  return status === 'completed' || status === 'failed';
}

function isPlayableAudioUrl(url: string | null | undefined) {
  return Boolean(
    url && !url.startsWith('local://') && !url.startsWith('mimo://'),
  );
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function formatPlaybackTime(seconds: number | null | undefined) {
  const safeSeconds =
    Number.isFinite(seconds) && seconds ? Math.max(0, Math.floor(seconds)) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const restSeconds = safeSeconds % 60;
  return `${minutes}:${restSeconds.toString().padStart(2, '0')}`;
}

function getAudioGenerationStage(isSubmitting: boolean, elapsedSec: number) {
  if (isSubmitting) {
    return {
      percent: 8,
      label: '提交准备请求',
      detail: '正在把本节内容交给语音服务。',
    };
  }
  if (elapsedSec < 10) {
    return {
      percent: 18,
      label: '排队中',
      detail: '已开始准备，完成后会自动切换为播放。',
    };
  }
  if (elapsedSec < 45) {
    return {
      percent: 38,
      label: '整理音频脚本',
      detail: '正在把本节真知和实践说明整理成音频稿。',
    };
  }
  if (elapsedSec < 120) {
    return {
      percent: 68,
      label: '合成语音',
      detail: '正在准备语音，页面会持续刷新状态。',
    };
  }
  if (elapsedSec < 180) {
    return {
      percent: 86,
      label: '保存音频',
      detail: '音频正在保存，稍后会出现播放按钮。',
    };
  }
  return {
    percent: 92,
    label: '等待回传',
    detail: '准备时间略久，可以先看详情，音频完成后会自动更新。',
  };
}

function getAudioUnavailableMessage(status: string, isPreparing: boolean) {
  if (isPreparing || status === 'processing')
    return '音频正在提前准备，完成后会自动出现播放。';
  if (status === 'failed') return '上次准备失败，可以重新准备。';
  return '音频还在排队，文字内容可以先看。';
}

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function visualImageUrl(ref: Record<string, unknown> | undefined) {
  const record = recordValue(ref);
  for (const key of ['url', 'cdnUrl', 'ossUrl', 'signedUrl']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

function visualDescriptor(ref: Record<string, unknown> | undefined) {
  const record = recordValue(ref);
  const assetKey = typeof record.assetKey === 'string' ? record.assetKey : '';
  const palette = typeof record.palette === 'string' ? record.palette : '';
  if (assetKey.includes('report')) return '报告线索';
  if (assetKey.includes('sleep') || palette.includes('sleep')) return '睡眠恢复';
  if (assetKey.includes('evidence') || palette.includes('evidence')) return '证据地图';
  if (assetKey.includes('movement') || palette.includes('movement')) return '行动底盘';
  if (palette.includes('safety')) return '安全边界';
  if (palette.includes('recovery')) return '身体余量';
  if (palette.includes('action')) return '今日行动';
  return '课程图片';
}

function KnowledgePulseVisual({
  className,
  visualRef,
  altText,
  label,
  badge,
  icon,
  title,
}: {
  className: string;
  visualRef?: Record<string, unknown>;
  altText?: string;
  label: string;
  badge: string;
  icon: 'sparkles' | 'headphones' | 'book';
  title: string;
}) {
  const imageUrl = visualImageUrl(visualRef);
  const descriptor = visualDescriptor(visualRef);
  const Icon =
    icon === 'sparkles' ? Sparkles : icon === 'book' ? BookOpen : Headphones;

  return (
    <div className={`${styles.pulseMedia} ${className}`}>
      {imageUrl && (
        <img
          className={styles.pulseMediaImage}
          src={imageUrl}
          alt={altText || `${title}课程图片`}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.hidden = true;
          }}
        />
      )}
      <div className={styles.pulseMediaBackdrop} aria-hidden="true" />
      <div className={styles.pulseMediaTop}>
        <span>{label}</span>
        <span>{badge}</span>
      </div>
      <div className={styles.pulseMediaCaption}>
        <span>{descriptor}</span>
        <strong>{title}</strong>
      </div>
      <div className={styles.pulseMediaIcon}>
        <Icon size={28} strokeWidth={1.4} aria-hidden />
      </div>
    </div>
  );
}

export default function HealthKnowledgePage() {
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const queuedAudioWarmupsRef = useRef<Set<string>>(new Set());
  const completingLessonIdsRef = useRef<Set<string>>(new Set());
  const [initialHomeCache] = useState(() =>
    getCachedCustomCourseHub({ allowStale: true }),
  );
  const [initialSeriesCache] = useState(() =>
    getCachedPodcastSeries({ allowStale: true }),
  );
  const [plan, setPlan] = useState<LongevityPlan | null>(
    initialHomeCache?.plan ?? null,
  );
  const [days, setDays] = useState<LongevityPlanDaysResponse['days']>(
    initialHomeCache?.days ?? [],
  );
  const [personalizedQueue, setPersonalizedQueue] =
    useState<PersonalizedKnowledgeQueueResponse | null>(
      initialHomeCache?.queue ?? null,
    );
  const [series, setSeries] = useState<PodcastSeries[]>(
    initialSeriesCache ?? [],
  );
  const [selectedQueueItemId, setSelectedQueueItemId] = useState<string | null>(
    null,
  );
  const [cardFeedback, setCardFeedback] = useState<
    Record<string, KnowledgeCardFeedback>
  >({});
  const [featuredGeneralEpisodes, setFeaturedGeneralEpisodes] = useState<
    Record<string, PodcastEpisodeWithProgress | null>
  >({});
  const [prewarmingAudioIds, setPrewarmingAudioIds] = useState<string[]>([]);
  const [todayAudio, setTodayAudio] = useState<LongevityAudio | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isGeneratingTodayAudio, setIsGeneratingTodayAudio] = useState(false);
  const [isCompletingCurrentLesson, setIsCompletingCurrentLesson] =
    useState(false);
  const [audioGenerationStartedAt, setAudioGenerationStartedAt] =
    useState<number | null>(null);
  const [audioGenerationTick, setAudioGenerationTick] = useState(Date.now());
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDurationSec, setAudioDurationSec] = useState(0);
  const [playbackRate, setPlaybackRate] =
    useState<(typeof PLAYBACK_RATES)[number]>(1);
  const [isLoading, setIsLoading] = useState(!initialHomeCache);
  const [isSeriesLoading, setIsSeriesLoading] = useState(!initialSeriesCache);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [seriesLoadError, setSeriesLoadError] = useState<string | null>(null);

  const loadKnowledgeHome = (silent = false) => {
    if (!silent) setIsLoading(true);
    if (!silent) setLoadError(null);

    void withKnowledgeTimeout(
      loadHomeSnapshot(),
      HOME_SNAPSHOT_TIMEOUT_MS,
      '个性化真知暂时没有读回来，请稍后重新读取。',
    )
      .then((snapshot) => {
        setPlan(snapshot.plan);
        setDays(snapshot.days);
        setPersonalizedQueue(snapshot.queue ?? null);
        setCachedCustomCourseHub(snapshot);
        setLoadError(null);
      })
      .catch((error) => {
        if (error instanceof KnowledgeHomeTimeoutError)
          homeSnapshotRequest = null;
        if (!silent)
          setLoadError(getLoadErrorMessage(error, '真知内容加载失败'));
      })
      .finally(() => {
        setIsLoading(false);
      });

    if (!silent) setIsSeriesLoading(true);
    if (!silent) setSeriesLoadError(null);
    void withKnowledgeTimeout(
      loadPodcastSeries(),
      SERIES_TIMEOUT_MS,
      '通识课暂时没有读回来。',
    )
      .then((nextSeries) => {
        setSeries(nextSeries);
        setCachedPodcastSeries(nextSeries);
        setSeriesLoadError(null);
      })
      .catch((error) => {
        if (error instanceof KnowledgeHomeTimeoutError)
          podcastSeriesRequest = null;
        if (!silent) {
          setSeries([]);
          setSeriesLoadError(getLoadErrorMessage(error, '通识课加载失败'));
        }
      })
      .finally(() => {
        setIsSeriesLoading(false);
      });
  };

  useEffect(() => {
    loadKnowledgeHome(Boolean(initialHomeCache || initialSeriesCache));
  }, []);

  useEffect(() => {
    const visibleSeries = series.slice(0, GENERAL_PULSE_CARD_LIMIT);
    const missingSeries = visibleSeries.filter(
      (item) => !(item.id in featuredGeneralEpisodes),
    );
    if (missingSeries.length === 0) return undefined;

    let cancelled = false;
    void Promise.all(
      missingSeries.map(async (item) => {
        try {
          const detail = await knowledgeApi.getPodcastSeriesDetail(item.id);
          return [item.id, detail.episodes[0] ?? null] as const;
        } catch {
          return [item.id, null] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setFeaturedGeneralEpisodes((current) => {
        const next = { ...current };
        entries.forEach(([seriesId, episode]) => {
          next[seriesId] = episode;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [featuredGeneralEpisodes, series]);

  const goals = plan ? getPlanGoalCards(plan) : [];
  const currentDay = plan?.currentDay ?? 1;
  const queueItems = personalizedQueue?.items ?? [];
  const selectedQueueItem = selectedQueueItemId
    ? queueItems.find((item) => item.id === selectedQueueItemId)
    : undefined;
  const currentQueueItem =
    selectedQueueItem ??
    queueItems.find((item) => item.id === personalizedQueue?.currentLessonId) ??
    queueItems[0];
  const currentLesson =
    (currentQueueItem
      ? days.find((item) => item.dayId === currentQueueItem.id)
      : undefined) ??
    days.find((item) => item.dayNumber === currentDay) ??
    days[0];
  const currentLessonTitle =
    currentQueueItem?.title ??
    (currentLesson ? getLessonTitle(currentLesson, goals) : '你的下一节长寿课');
  const hasPersonalPath = Boolean((plan && currentLesson) || currentQueueItem);
  const isPathLoading = isLoading && !hasPersonalPath;
  const currentPlanId = currentQueueItem?.sourceRef.planId ?? plan?.id;
  const currentLessonDay = currentLesson?.dayNumber;
  const todayAudioUrl = todayAudio?.url ?? null;
  const audioStatus =
    todayAudio?.status ??
    currentQueueItem?.audioStatus ??
    currentLesson?.artifactStatuses.podcastAudio ??
    'pending';
  const isCurrentAudioPrewarming = currentQueueItem
    ? prewarmingAudioIds.includes(currentQueueItem.id)
    : false;
  const canPlayTodayAudio = isPlayableAudioUrl(todayAudioUrl);
  const isTodayAudioProcessing =
    isGeneratingTodayAudio || audioStatus === 'processing' || isCurrentAudioPrewarming;
  const canGenerateTodayAudio = Boolean(
    currentPlanId &&
      currentLessonDay &&
      !canPlayTodayAudio &&
      !isTodayAudioProcessing,
  );
  const resolvedAudioDuration =
    audioDurationSec || todayAudio?.durationSec || 0;
  const audioProgressMax =
    resolvedAudioDuration > 0 ? resolvedAudioDuration : 1;
  const audioProgressValue = Math.min(audioCurrentTime, audioProgressMax);
  const audioGenerationElapsedSec =
    audioGenerationStartedAt && isTodayAudioProcessing
      ? Math.max(
          0,
          Math.floor((audioGenerationTick - audioGenerationStartedAt) / 1000),
        )
      : 0;
  const audioGenerationStage = getAudioGenerationStage(
    isGeneratingTodayAudio,
    audioGenerationElapsedSec,
  );
  const playButtonLabel = canPlayTodayAudio
    ? isAudioPlaying
      ? '暂停'
      : '播放'
    : isTodayAudioProcessing
      ? '准备中'
      : audioStatus === 'failed'
        ? '重新准备'
        : '准备音频';
  const pulseFeedSubtitle = hasPersonalPath
    ? '个性化课节优先，通识课只放两张作为背景补充。'
    : isPathLoading
      ? '稍后会显示今日真知和通识课。'
      : '还没建立个人路径时，可以先听入门课建立判断力。';
  const topicSuggestions = useMemo(
    () =>
      buildQueueSuggestions(queueItems, buildTopicSuggestions(currentLesson, goals)),
    [currentLesson, goals, queueItems],
  );
  const personalizedPulseCards = topicSuggestions.slice(
    0,
    PERSONALIZED_PULSE_CARD_LIMIT,
  );
  const generalPulseCards = series.slice(0, GENERAL_PULSE_CARD_LIMIT);
  const currentActionLabel =
    currentQueueItem?.actionLabel ?? currentLesson?.taskTitle ?? '当前行动';
  const todayKnowledgeContext: AeternaLoopContextValue | null =
    (currentLesson && plan) || currentQueueItem
      ? {
          sourceType: 'podcast',
          sourceId: currentQueueItem
            ? `${currentQueueItem.sourceRef.planId}:${currentQueueItem.id}`
            : `${plan?.id}:${currentLesson?.dayId}`,
          sourceTitle: currentLessonTitle,
          stage: 'understand',
          evidenceBoundary:
            personalizedQueue?.evidenceBoundary.safetyNote ??
            '根据当前百岁目标、实践安排和近期健康线索生成；用于解释为什么这样开始，不替代医学诊断。',
          askPrompt: `我正在听个性化真知《${currentLessonTitle}》。请结合我的百岁目标和当前实践，解释为什么先做「${currentActionLabel}」，以及做到什么程度就够。`,
        }
      : null;
  const buildPersonalizedPulseContext = (
    topic: KnowledgeQueueSuggestion,
  ): AeternaLoopContextValue => {
    const queueItem = queueItems.find((item) => item.id === topic.id);
    const actionLabel = queueItem?.actionLabel ?? currentActionLabel;
    return {
      sourceType: 'podcast',
      sourceId: queueItem
        ? `${queueItem.sourceRef.planId}:${queueItem.id}`
        : todayKnowledgeContext?.sourceId,
      sourceTitle: topic.title,
      stage: 'understand',
      evidenceBoundary:
        personalizedQueue?.evidenceBoundary.safetyNote ??
        '根据当前百岁目标、实践安排和近期健康线索生成；用于解释为什么这样开始，不替代医学诊断。',
      askPrompt: `我正在看今日真知卡《${topic.title}》。请结合我的百岁目标和当前实践，解释它和「${actionLabel}」的关系；如果资料不足，请先说明缺口。`,
    };
  };
  const hasPathLoadFailure = Boolean(loadError) && !hasPersonalPath;
  const shouldShowSkeleton =
    isLoading && isSeriesLoading && !initialHomeCache && !initialSeriesCache;

  useEffect(() => {
    if (!currentPlanId || !currentLessonDay) {
      setTodayAudio(null);
      setIsAudioPlaying(false);
      setIsGeneratingTodayAudio(false);
      setAudioCurrentTime(0);
      setAudioDurationSec(0);
      return;
    }

    const cachedLesson = getCachedCustomCourseLesson(
      currentPlanId,
      currentLessonDay,
    );
    if (cachedLesson) {
      setTodayAudio(cachedLesson.audio);
      setAudioDurationSec(cachedLesson.audio.durationSec ?? 0);
    } else {
      setTodayAudio(null);
      setAudioDurationSec(0);
    }
    setIsAudioPlaying(false);
    setIsGeneratingTodayAudio(false);
    setAudioCurrentTime(0);

    let cancelled = false;
    void executeApi
      .getLongevityDay(currentPlanId, currentLessonDay)
      .then((lesson) => {
        if (cancelled) return;
        if (
          currentQueueItem &&
          prewarmingAudioIds.includes(currentQueueItem.id) &&
          lesson.audio.status !== 'completed' &&
          lesson.audio.status !== 'failed' &&
          lesson.audio.status !== 'processing'
        ) {
          return;
        }
        setCachedCustomCourseLesson(currentPlanId, currentLessonDay, lesson);
        setTodayAudio(lesson.audio);
        setAudioDurationSec(lesson.audio.durationSec ?? 0);
        if (currentQueueItem && isAudioTerminal(lesson.audio.status)) {
          setPrewarmingAudioIds((ids) =>
            ids.filter((item) => item !== currentQueueItem.id),
          );
        }
      })
      .catch(() => {
        if (!cancelled && !cachedLesson) setTodayAudio(null);
      });

    return () => {
      cancelled = true;
    };
  }, [currentPlanId, currentLessonDay, currentQueueItem, prewarmingAudioIds]);

  useEffect(() => {
    if (!KNOWLEDGE_AUDIO_AUTOPREWARM_ENABLED) return;
    if (isLoading || days.length === 0 || queueItems.length === 0) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        for (const item of queueItems) {
          if (cancelled) return;
          if (!shouldPrewarmAudio(item.audioStatus)) continue;
          const lesson = days.find((candidate) => candidate.dayId === item.id);
          const dayNumber = lesson?.dayNumber;
          if (!dayNumber) continue;
          const key = `${item.sourceRef.planId}:${dayNumber}`;
          if (queuedAudioWarmupsRef.current.has(key)) continue;

          queuedAudioWarmupsRef.current.add(key);
          setPrewarmingAudioIds((ids) =>
            ids.includes(item.id) ? ids : [...ids, item.id],
          );

          try {
            const response = await executeApi.generateLongevityDayAudio(
              item.sourceRef.planId,
              dayNumber,
            );
            if (cancelled) return;
            setPersonalizedQueue((queue) =>
              queue
                ? {
                    ...queue,
                    items: queue.items.map((queueItem) =>
                      queueItem.id === item.id
                        ? {
                            ...queueItem,
                            audioStatus: response.audio.status,
                            readiness: readinessFromAudioStatus(
                              response.audio.status,
                            ),
                          }
                        : queueItem,
                    ),
                  }
                : queue,
            );
            if (currentQueueItem?.id === item.id) {
              setTodayAudio(response.audio);
              setAudioDurationSec(response.audio.durationSec ?? 0);
            }
            if (isAudioTerminal(response.audio.status)) {
              setPrewarmingAudioIds((ids) =>
                ids.filter((id) => id !== item.id),
              );
            }
          } catch {
            queuedAudioWarmupsRef.current.delete(key);
            setPrewarmingAudioIds((ids) =>
              ids.filter((id) => id !== item.id),
            );
          }

          await wait(AUDIO_PREWARM_SPACING_MS);
        }
      })();
    }, AUDIO_PREWARM_START_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [currentQueueItem, days, isLoading, queueItems]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate, todayAudioUrl]);

  useEffect(() => {
    if (!isTodayAudioProcessing) {
      setAudioGenerationStartedAt(null);
      return;
    }

    const parsedUpdatedAt = todayAudio?.updatedAt
      ? Date.parse(todayAudio.updatedAt)
      : Number.NaN;
    setAudioGenerationStartedAt((current) =>
      current ??
      (Number.isFinite(parsedUpdatedAt) ? parsedUpdatedAt : Date.now()),
    );
    setAudioGenerationTick(Date.now());

    const timer = window.setInterval(() => {
      setAudioGenerationTick(Date.now());
    }, 1_000);

    return () => window.clearInterval(timer);
  }, [isTodayAudioProcessing, todayAudio?.updatedAt]);

  useEffect(() => {
    if (!isTodayAudioProcessing || !currentPlanId || !currentLessonDay) return;

    let cancelled = false;
    const refreshAudio = async () => {
      try {
        const lesson = await executeApi.getLongevityDay(
          currentPlanId,
          currentLessonDay,
        );
        if (cancelled) return;
        if (
          currentQueueItem &&
          prewarmingAudioIds.includes(currentQueueItem.id) &&
          lesson.audio.status !== 'completed' &&
          lesson.audio.status !== 'failed' &&
          lesson.audio.status !== 'processing'
        ) {
          return;
        }
        setCachedCustomCourseLesson(currentPlanId, currentLessonDay, lesson);
        setTodayAudio(lesson.audio);
        setAudioDurationSec(lesson.audio.durationSec ?? 0);
        if (currentQueueItem) {
          setPersonalizedQueue((queue) =>
            queue
              ? {
                  ...queue,
                  items: queue.items.map((item) =>
                    item.id === currentQueueItem.id
                      ? {
                          ...item,
                          audioStatus: lesson.audio.status,
                          readiness: readinessFromAudioStatus(
                            lesson.audio.status,
                          ),
                        }
                      : item,
                  ),
                }
              : queue,
          );
          if (isAudioTerminal(lesson.audio.status)) {
            setPrewarmingAudioIds((ids) =>
              ids.filter((id) => id !== currentQueueItem.id),
            );
          }
        }
        if (
          isPlayableAudioUrl(lesson.audio.url) &&
          lesson.audio.status === 'completed'
        ) {
          showToast('音频已准备好');
        }
      } catch {
        // 保持当前生成进度；下一轮继续轮询。
      }
    };

    const startTimer = window.setTimeout(refreshAudio, 1_500);
    const interval = window.setInterval(refreshAudio, AUDIO_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      window.clearInterval(interval);
    };
  }, [
    currentPlanId,
    currentLessonDay,
    currentQueueItem,
    isTodayAudioProcessing,
    prewarmingAudioIds,
  ]);

  const toggleAudioPlayback = async () => {
    if (!audioRef.current || !canPlayTodayAudio) return;
    if (isAudioPlaying) {
      audioRef.current.pause();
      return;
    }
    try {
      audioRef.current.playbackRate = playbackRate;
      await audioRef.current.play();
    } catch {
      showToast('音频暂时无法播放');
    }
  };

  const generateTodayAudio = async () => {
    if (!currentPlanId || !currentLessonDay || !canGenerateTodayAudio) return;
    setIsGeneratingTodayAudio(true);
    try {
      const response = await executeApi.generateLongevityDayAudio(
        currentPlanId,
        currentLessonDay,
      );
      setTodayAudio(response.audio);
      setAudioDurationSec(response.audio.durationSec ?? 0);
      showToast(response.audio.url ? '音频已准备好' : '已开始准备音频');
    } catch (error) {
      showToast(error instanceof Error ? error.message : '音频准备失败');
      try {
        const dayPackage = await executeApi.getLongevityDay(
          currentPlanId,
          currentLessonDay,
        );
        setTodayAudio(dayPackage.audio);
        setAudioDurationSec(dayPackage.audio.durationSec ?? 0);
      } catch {
        // Keep the local optimistic state if the follow-up refresh also fails.
      }
    } finally {
      setIsGeneratingTodayAudio(false);
    }
  };

  const handleAudioPrimaryAction = async () => {
    if (canPlayTodayAudio) {
      await toggleAudioPlayback();
      return;
    }
    await generateTodayAudio();
  };

  const seekTodayAudio = (value: string) => {
    const nextTime = Number(value);
    setAudioCurrentTime(nextTime);
    if (audioRef.current) audioRef.current.currentTime = nextTime;
  };

  const selectQueueCandidate = async (
    topic: KnowledgeQueueSuggestion,
  ) => {
    setSelectedQueueItemId(topic.id);
    const queueItem = queueItems.find((item) => item.id === topic.id);
    try {
      if (queueItem) {
        const queue = await knowledgeApi.selectPersonalizedQueueItem({
          planId: queueItem.sourceRef.planId,
          lessonId: queueItem.id,
        });
        setPersonalizedQueue(queue);
        setCachedCustomCourseHub({ plan, days, queue });
      }
      const response = await chatApi.updateKnowledgePreference({
        eventType: 'topic_interest',
        topicTitle: topic.title,
        topicKey: 'knowledge_course',
        selected: true,
        reason: topic.reason,
      });
      showToast(
        response.learningEnabled
          ? '已切换到这一节'
          : '已在本页记录；AI 学习已关闭',
      );
    } catch {
      showToast('这节暂时切换失败，请稍后重试');
    }
  };

  const recordCardFeedback = async ({
    cardId,
    type,
    title,
    topicKey,
    reason,
    sourceId,
    courseId,
    lessonDay,
  }: {
    cardId: string;
    type: KnowledgeCardFeedback;
    title: string;
    topicKey: string;
    reason: string;
    sourceId?: string | undefined;
    courseId?: string | undefined;
    lessonDay?: number | undefined;
  }) => {
    setCardFeedback((feedback) => ({
      ...feedback,
      [cardId]: type,
    }));
    try {
      const response = await chatApi.updateKnowledgePreference({
        eventType: 'topic_interest',
        topicTitle: title,
        topicKey,
        selected: type !== 'dismissed',
        reason,
        feedback: type,
        sourceTitle: title,
        sourceId,
        courseId,
        lessonDay,
      });
      showToast(getFeedbackToast(type, response.learningEnabled));
    } catch {
      showToast('已在本页记录，稍后再同步偏好');
    }
  };

  const completeCurrentLesson = async () => {
    if (!currentQueueItem || isCompletingCurrentLesson) return;
    const completedLessonId = currentQueueItem.id;
    const completedIndex = queueItems.findIndex(
      (item) => item.id === completedLessonId,
    );
    if (completingLessonIdsRef.current.has(completedLessonId)) return;
    completingLessonIdsRef.current.add(completedLessonId);
    setIsCompletingCurrentLesson(true);
    try {
      const queue = await knowledgeApi.completePersonalizedQueueItem({
        planId: currentQueueItem.sourceRef.planId,
        lessonId: completedLessonId,
      });
      setPersonalizedQueue(queue);
      const replacementLessonId =
        completedIndex >= 0 ? queue.items[completedIndex]?.id : undefined;
      setSelectedQueueItemId(
        replacementLessonId ?? queue.currentLessonId ?? null,
      );
      setCachedCustomCourseHub({ plan, days, queue });
      showToast('已放入历史，新的线索已接上');
    } catch {
      showToast('状态暂时没有写入，请稍后重试');
    } finally {
      completingLessonIdsRef.current.delete(completedLessonId);
      setIsCompletingCurrentLesson(false);
    }
  };

  const completeCurrentLessonIfAudioFinished = (
    audio: HTMLAudioElement,
  ) => {
    if (!currentQueueItem || isCompletingCurrentLesson) return;
    const duration = Number.isFinite(audio.duration)
      ? audio.duration
      : resolvedAudioDuration;
    if (!duration || duration < 1) return;
    const remainingSec = duration - audio.currentTime;
    if (remainingSec <= 1.5) void completeCurrentLesson();
  };

  const renderCurrentAudioPlayer = () => (
    <>
      {canPlayTodayAudio && (
        <audio
          className={styles.hiddenAudio}
          ref={audioRef}
          src={todayAudioUrl ?? undefined}
          preload="metadata"
          onLoadedMetadata={(event) => {
            const duration = event.currentTarget.duration;
            setAudioDurationSec(
              Number.isFinite(duration)
                ? duration
                : (todayAudio?.durationSec ?? 0),
            );
          }}
          onTimeUpdate={(event) => {
            setAudioCurrentTime(event.currentTarget.currentTime);
            completeCurrentLessonIfAudioFinished(event.currentTarget);
          }}
          onPlay={() => setIsAudioPlaying(true)}
          onPause={() => setIsAudioPlaying(false)}
          onEnded={() => {
            setIsAudioPlaying(false);
            void completeCurrentLesson();
          }}
        />
      )}
      <div className={styles.homeAudioPlayer}>
        <button
          className={styles.audioPlayButton}
          type="button"
          disabled={!canPlayTodayAudio && !canGenerateTodayAudio}
          onClick={handleAudioPrimaryAction}
          aria-label={playButtonLabel}
        >
          {isTodayAudioProcessing ? (
            <Sparkles size={18} strokeWidth={1.6} aria-hidden />
          ) : isAudioPlaying ? (
            <Pause size={18} strokeWidth={1.6} aria-hidden />
          ) : (
            <Play size={18} strokeWidth={1.6} aria-hidden />
          )}
          <span>{playButtonLabel}</span>
        </button>
        <div className={styles.audioProgressGroup}>
          {isTodayAudioProcessing ? (
            <div className={styles.generationProgress} aria-live="polite">
              <div className={styles.generationProgressHeader}>
                <span>{audioGenerationStage.label}</span>
                <span>{audioGenerationStage.percent}%</span>
              </div>
              <progress
                className={styles.generationProgressBar}
                max={100}
                value={audioGenerationStage.percent}
                aria-label="音频准备进度"
              />
              <span className={styles.audioTime}>
                已等待 {formatPlaybackTime(audioGenerationElapsedSec)}
              </span>
            </div>
          ) : canPlayTodayAudio ? (
            <div className={styles.audioPlaybackProgress}>
              <input
                className={styles.audioProgress}
                type="range"
                min={0}
                max={audioProgressMax}
                step={1}
                value={audioProgressValue}
                aria-label="播放进度"
                onChange={(event) => seekTodayAudio(event.currentTarget.value)}
              />
              <span className={styles.audioTime}>
                {formatPlaybackTime(audioCurrentTime)} /{' '}
                {formatPlaybackTime(resolvedAudioDuration)}
              </span>
            </div>
          ) : (
            <span className={styles.audioStatusText}>
              {getAudioUnavailableMessage(audioStatus, isTodayAudioProcessing)}
            </span>
          )}
        </div>
        <div className={styles.speedControl} aria-label="倍速控制">
          {PLAYBACK_RATES.map((rate) => (
            <button
              className={
                playbackRate === rate
                  ? styles.speedButtonActive
                  : styles.speedButton
              }
              type="button"
              key={rate}
              disabled={!canPlayTodayAudio}
              aria-pressed={playbackRate === rate}
              onClick={() => setPlaybackRate(rate)}
            >
              {rate}x
            </button>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        mode="primary"
        title="真知"
        subtitle="把医学知识讲清楚"
        showProfile
      />
      <main className={styles.content}>
        {shouldShowSkeleton ? (
          <KnowledgeHomeSkeleton />
        ) : (
          <div className={styles.homeLoaded}>
            {loadError && hasPersonalPath && (
              <article className={styles.seriesCard}>
                <h2 className={styles.cardTitle}>真知内容暂时加载失败。</h2>
                <p className={styles.cardText}>{loadError}</p>
              </article>
            )}

            <div className={styles.homeStack}>
              <div className={styles.homeMain}>
                <section className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <div>
                      <p className={styles.eyebrow}>今日真知</p>
                      <h2 className={styles.sectionTitle}>为你整理</h2>
                      <p className={styles.sectionSubtitle}>
                        {pulseFeedSubtitle}
                      </p>
                    </div>
                    {hasPersonalPath && (
                      <button
                        className={styles.quietButton}
                        type="button"
                        onClick={() => navigate('/knowledge/personalized/history')}
                      >
                        <History size={16} strokeWidth={1.5} aria-hidden />
                        历史课程
                      </button>
                    )}
                  </div>

                  <div className={styles.pulseFeed}>
                    {hasPersonalPath ? (
                      personalizedPulseCards.map((topic, index) => {
                        const queueItem = queueItems.find(
                          (item) => item.id === topic.id,
                        );
                        const cardDay = queueItem
                          ? days.find((day) => day.dayId === queueItem.id)
                          : currentLesson;
                        const isSelected =
                          currentQueueItem?.id === topic.id ||
                          selectedQueueItemId === topic.id ||
                          (!currentQueueItem &&
                            !selectedQueueItemId &&
                            index === 0);
                        const readiness = prewarmingAudioIds.includes(topic.id)
                          ? 'audio_processing'
                          : topic.readiness;
                        const cardContext = buildPersonalizedPulseContext(topic);
                        const cardKey = `personalized-${topic.id}`;
                        const feedbackState = cardFeedback[cardKey];

                        return (
                          <article
                            className={`${styles.pulseCard} ${
                              isSelected ? styles.pulseCardActive : ''
                            }`}
                            key={topic.id}
                            aria-label={`个性化真知卡：${topic.title}`}
                          >
                            <KnowledgePulseVisual
                              className={styles.pulseMediaPersonal}
                              visualRef={queueItem?.imageRef ?? topic.imageRef}
                              altText={queueItem?.imageAltText ?? topic.imageAltText}
                              label={queueLabelForIndex(index)}
                              badge={isSelected ? '当前' : '个性化'}
                              icon="sparkles"
                              title={topic.title}
                            />
                            <div className={styles.pulseCardBody}>
                              <div className={styles.pulseCardHeader}>
                                <span className={styles.lessonMeta}>
                                  {topic.reason}
                                </span>
                                <span className={styles.statusPill}>
                                  {isSelected
                                    ? `当前 · ${getReadinessLabel(readiness)}`
                                    : getReadinessLabel(readiness)}
                                </span>
                              </div>
                              <h3 className={styles.pulseCardTitle}>
                                {topic.title}
                              </h3>
                              <p className={styles.pulseCardText}>
                                {topic.summary ??
                                  '这一张卡只讲清楚：它为什么和今天的行动有关，以及做到什么程度就够。'}
                              </p>

                              {isSelected && renderCurrentAudioPlayer()}

                              <div className={styles.pulseActions}>
                                {!isSelected && (
                                  <button
                                    className={styles.quietButton}
                                    type="button"
                                    onClick={() => selectQueueCandidate(topic)}
                                  >
                                    展开这节
                                    <ArrowRight
                                      size={16}
                                      strokeWidth={1.5}
                                      aria-hidden
                                    />
                                  </button>
                                )}
                                <ContextAskButton
                                  className={styles.quietButton}
                                  context={cardContext}
                                  label="问这张卡"
                                  onNavigate={navigate}
                                />
                                {isSelected && currentQueueItem && (
                                  <button
                                    className={styles.quietButton}
                                    type="button"
                                    disabled={isCompletingCurrentLesson}
                                    onClick={completeCurrentLesson}
                                  >
                                    <CheckCircle2
                                      size={16}
                                      strokeWidth={1.5}
                                      aria-hidden
                                    />
                                    {isCompletingCurrentLesson
                                      ? '写入中'
                                      : '听懂了'}
                                  </button>
                                )}
                              </div>

                              <div
                                className={styles.pulseFeedbackRow}
                                aria-label="卡片反馈"
                              >
                                <button
                                  className={
                                    feedbackState === 'helpful'
                                      ? styles.pulseIconButtonActive
                                      : styles.pulseIconButton
                                  }
                                  type="button"
                                  aria-pressed={feedbackState === 'helpful'}
                                  onClick={() =>
                                    void recordCardFeedback({
                                      cardId: cardKey,
                                      type: 'helpful',
                                      title: topic.title,
                                      topicKey: 'knowledge_course',
                                      reason: topic.reason,
                                      sourceId: cardContext.sourceId,
                                      lessonDay: cardDay?.dayNumber,
                                    })
                                  }
                                >
                                  <ThumbsUp
                                    size={16}
                                    strokeWidth={1.5}
                                    aria-hidden
                                  />
                                  有帮助
                                </button>
                                <button
                                  className={
                                    feedbackState === 'saved'
                                      ? styles.pulseIconButtonActive
                                      : styles.pulseIconButton
                                  }
                                  type="button"
                                  aria-pressed={feedbackState === 'saved'}
                                  onClick={() =>
                                    void recordCardFeedback({
                                      cardId: cardKey,
                                      type: 'saved',
                                      title: topic.title,
                                      topicKey: 'knowledge_course_saved',
                                      reason: '用户希望保留这张个性化真知卡',
                                      sourceId: cardContext.sourceId,
                                      lessonDay: cardDay?.dayNumber,
                                    })
                                  }
                                >
                                  <Bookmark
                                    size={16}
                                    strokeWidth={1.5}
                                    aria-hidden
                                  />
                                  保存
                                </button>
                                <button
                                  className={
                                    feedbackState === 'dismissed'
                                      ? styles.pulseIconButtonActive
                                      : styles.pulseIconButton
                                  }
                                  type="button"
                                  aria-pressed={feedbackState === 'dismissed'}
                                  onClick={() =>
                                    void recordCardFeedback({
                                      cardId: cardKey,
                                      type: 'dismissed',
                                      title: topic.title,
                                      topicKey: 'knowledge_course',
                                      reason: '用户暂时不想看这类个性化真知',
                                      sourceId: cardContext.sourceId,
                                      lessonDay: cardDay?.dayNumber,
                                    })
                                  }
                                >
                                  <ThumbsDown
                                    size={16}
                                    strokeWidth={1.5}
                                    aria-hidden
                                  />
                                  暂时不看
                                </button>
                              </div>
                            </div>
                          </article>
                        );
                      })
                    ) : (
                      <article
                        className={`${styles.pulseCard} ${styles.firstRunCard}`}
                      >
                        <div
                          className={`${styles.pulseMedia} ${styles.pulseMediaPersonal}`}
                        >
                          <div className={styles.pulseMediaTop}>
                            <span>01</span>
                            <span>个性化</span>
                          </div>
                          <div className={styles.pulseMediaIcon}>
                            <BookOpen
                              size={28}
                              strokeWidth={1.4}
                              aria-hidden
                            />
                          </div>
                        </div>
                        <div className={styles.pulseCardBody}>
                          <h2 className={styles.pulseCardTitle}>
                            {isLoading
                              ? '正在准备个性化真知。'
                              : hasPathLoadFailure
                                ? '个性化真知暂时没读出来。'
                                : '先建立你的实践路径。'}
                          </h2>
                          <p className={styles.pulseCardText}>
                            {isLoading
                              ? '稍后会显示要理解的一件事，以及对应的小实践。'
                              : hasPathLoadFailure
                                ? loadError
                                : '真知不是让你先背课程。先告诉它你想保留哪种能力，它才会把知识讲成现在能做的一件小事。'}
                          </p>
                          {!isLoading && !hasPathLoadFailure && (
                            <div className={styles.firstRunSteps}>
                              <span>先选百岁目标</span>
                              <span>保持三节候选</span>
                              <span>听懂后去践行</span>
                            </div>
                          )}
                          {!isLoading && hasPathLoadFailure && (
                            <button
                              className={styles.primaryButton}
                              type="button"
                              onClick={() => loadKnowledgeHome(false)}
                            >
                              重新读取
                              <RotateCcw
                                size={16}
                                strokeWidth={1.5}
                                aria-hidden
                              />
                            </button>
                          )}
                          {!isLoading && !hasPathLoadFailure && (
                            <button
                              className={styles.primaryButton}
                              type="button"
                              onClick={() =>
                                navigate('/execute/longevity/create')
                              }
                            >
                              创建百岁计划
                              <ArrowRight
                                size={16}
                                strokeWidth={1.5}
                                aria-hidden
                              />
                            </button>
                          )}
                        </div>
                      </article>
                    )}

                    {isSeriesLoading && (
                      <article
                        className={`${styles.pulseCard} ${styles.pulseCardMuted}`}
                        aria-busy="true"
                      >
                        <div
                          className={`${styles.pulseMedia} ${styles.pulseMediaGeneral}`}
                        >
                          <div className={styles.pulseMediaTop}>
                            <span>通识</span>
                            <span>读取中</span>
                          </div>
                          <div className={styles.pulseMediaIcon}>
                            <Headphones
                              size={28}
                              strokeWidth={1.4}
                              aria-hidden
                            />
                          </div>
                        </div>
                        <div className={styles.pulseCardBody}>
                          <span className={styles.lessonMeta}>正在读取</span>
                          <h2 className={styles.pulseCardTitle}>
                            通识课会稍后显示
                          </h2>
                          <p className={styles.pulseCardText}>
                            个性化真知可以先听。
                          </p>
                        </div>
                      </article>
                    )}
                    {!isSeriesLoading &&
                      generalPulseCards.map((item, index) => {
                        const featuredEpisode =
                          featuredGeneralEpisodes[item.id] ?? null;
                        const cardTitle = getGeneralPulseTitle(
                          item,
                          featuredEpisode,
                        );
                        const cardSummary = getGeneralPulseSummary(
                          item,
                          featuredEpisode,
                        );
                        const cardPath = getGeneralPulsePath(
                          item,
                          featuredEpisode,
                        );
                        const isEpisodeCard = Boolean(featuredEpisode);
                        const cardKey = `general-${featuredEpisode?.id ?? item.id}`;
                        const feedbackState = cardFeedback[cardKey];
                        const cardContext = getGeneralPulseContext(
                          item,
                          featuredEpisode,
                        );

                        return (
                          <article
                            className={`${styles.pulseCard} ${styles.pulseCardGeneral}`}
                            key={item.id}
                            aria-label={`通识课程卡：${cardTitle}`}
                          >
                            <KnowledgePulseVisual
                              className={styles.pulseMediaGeneral}
                              visualRef={
                                featuredEpisode?.imageRef ??
                                item.imageRef ??
                                item.coverAssetRef
                              }
                              altText={
                                featuredEpisode?.imageAltText ??
                                item.imageAltText
                              }
                              label={String(index + 1).padStart(2, '0')}
                              badge={isEpisodeCard ? '精选单集' : '通识'}
                              icon="headphones"
                              title={cardTitle}
                            />
                            <div className={styles.pulseCardBody}>
                              <div className={styles.pulseCardHeader}>
                                <span className={styles.lessonMeta}>
                                  {isEpisodeCard ? '精选单集' : '背景补充'}
                                </span>
                                <span className={styles.statusPill}>
                                  {isEpisodeCard ? '单集' : '系列课'}
                                </span>
                              </div>
                              <span className={styles.lessonMeta}>
                                {getAudienceLevelLabel(item.audienceLevel)} ·
                                {isEpisodeCard ? '通识补充' : '不读取个人数值'}
                              </span>
                              <h3 className={styles.pulseCardTitle}>
                                {cardTitle}
                              </h3>
                              <p className={styles.pulseCardText}>
                                {cardSummary}
                              </p>
                              <div className={styles.pulseTagRow}>
                                {item.topicTags.slice(0, 3).map((tag) => (
                                  <span className={styles.tag} key={tag}>
                                    {tag}
                                  </span>
                                ))}
                              </div>
                              <div className={styles.pulseActions}>
                                <button
                                  className={styles.quietButton}
                                  type="button"
                                  onClick={() => navigate(cardPath)}
                                >
                                  {isEpisodeCard ? '进入单集' : '查看课程'}
                                  <ArrowRight
                                    size={16}
                                    strokeWidth={1.5}
                                    aria-hidden
                                  />
                                </button>
                                <ContextAskButton
                                  className={styles.quietButton}
                                  context={cardContext}
                                  label="问这张卡"
                                  onNavigate={navigate}
                                />
                              </div>
                              <div
                                className={styles.pulseFeedbackRow}
                                aria-label="卡片反馈"
                              >
                                <button
                                  className={
                                    feedbackState === 'helpful'
                                      ? styles.pulseIconButtonActive
                                      : styles.pulseIconButton
                                  }
                                  type="button"
                                  aria-pressed={feedbackState === 'helpful'}
                                  onClick={() =>
                                    void recordCardFeedback({
                                      cardId: cardKey,
                                      type: 'helpful',
                                      title: cardTitle,
                                      topicKey: isEpisodeCard
                                        ? 'knowledge_general_episode'
                                        : 'knowledge_general_course',
                                      reason: isEpisodeCard
                                        ? '用户觉得这张通识单集卡有帮助'
                                        : '用户觉得这张通识课程卡有帮助',
                                      sourceId: featuredEpisode?.id ?? item.id,
                                      courseId: item.id,
                                    })
                                  }
                                >
                                  <ThumbsUp
                                    size={16}
                                    strokeWidth={1.5}
                                    aria-hidden
                                  />
                                  有帮助
                                </button>
                                <button
                                  className={
                                    feedbackState === 'saved'
                                      ? styles.pulseIconButtonActive
                                      : styles.pulseIconButton
                                  }
                                  type="button"
                                  aria-pressed={feedbackState === 'saved'}
                                  onClick={() =>
                                    void recordCardFeedback({
                                      cardId: cardKey,
                                      type: 'saved',
                                      title: cardTitle,
                                      topicKey: isEpisodeCard
                                        ? 'knowledge_general_episode_saved'
                                        : 'knowledge_general_course_saved',
                                      reason: isEpisodeCard
                                        ? '用户希望保留这张通识单集卡'
                                        : '用户希望保留这张通识课程卡',
                                      sourceId: featuredEpisode?.id ?? item.id,
                                      courseId: item.id,
                                    })
                                  }
                                >
                                  <Bookmark
                                    size={16}
                                    strokeWidth={1.5}
                                    aria-hidden
                                  />
                                  保存
                                </button>
                                <button
                                  className={
                                    feedbackState === 'dismissed'
                                      ? styles.pulseIconButtonActive
                                      : styles.pulseIconButton
                                  }
                                  type="button"
                                  aria-pressed={feedbackState === 'dismissed'}
                                  onClick={() =>
                                    void recordCardFeedback({
                                      cardId: cardKey,
                                      type: 'dismissed',
                                      title: cardTitle,
                                      topicKey: isEpisodeCard
                                        ? 'knowledge_general_episode'
                                        : 'knowledge_general_course',
                                      reason: isEpisodeCard
                                        ? '用户暂时不想看这类通识单集'
                                        : '用户暂时不想看这类通识课',
                                      sourceId: featuredEpisode?.id ?? item.id,
                                      courseId: item.id,
                                    })
                                  }
                                >
                                  <ThumbsDown
                                    size={16}
                                    strokeWidth={1.5}
                                    aria-hidden
                                  />
                                  暂时不看
                                </button>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    {!isSeriesLoading && seriesLoadError && (
                      <article
                        className={`${styles.pulseCard} ${styles.pulseCardMuted}`}
                      >
                        <div
                          className={`${styles.pulseMedia} ${styles.pulseMediaGeneral}`}
                        >
                          <div className={styles.pulseMediaTop}>
                            <span>通识</span>
                            <span>稍后</span>
                          </div>
                          <div className={styles.pulseMediaIcon}>
                            <Headphones
                              size={28}
                              strokeWidth={1.4}
                              aria-hidden
                            />
                          </div>
                        </div>
                        <div className={styles.pulseCardBody}>
                          <span className={styles.lessonMeta}>稍后重试</span>
                          <h2 className={styles.pulseCardTitle}>
                            通识课暂时没读出来
                          </h2>
                          <p className={styles.pulseCardText}>
                            {seriesLoadError}
                          </p>
                        </div>
                      </article>
                    )}
                    {!isSeriesLoading &&
                      !seriesLoadError &&
                      series.length === 0 && (
                        <article className={styles.pulseCard}>
                          <div
                            className={`${styles.pulseMedia} ${styles.pulseMediaGeneral}`}
                          >
                            <div className={styles.pulseMediaTop}>
                              <span>通识</span>
                              <span>整理中</span>
                            </div>
                            <div className={styles.pulseMediaIcon}>
                              <Headphones
                                size={28}
                                strokeWidth={1.4}
                                aria-hidden
                              />
                            </div>
                          </div>
                          <div className={styles.pulseCardBody}>
                            <span className={styles.lessonMeta}>
                              通识课 · 系列
                            </span>
                            <h2 className={styles.pulseCardTitle}>
                              通识课还在整理
                            </h2>
                            <p className={styles.pulseCardText}>
                              这里会放适合补基础的系列课。
                            </p>
                            <button
                              className={styles.quietButton}
                              type="button"
                              onClick={() => navigate('/knowledge/podcasts')}
                            >
                              查看通识课
                              <ArrowRight
                                size={16}
                                strokeWidth={1.5}
                                aria-hidden
                              />
                            </button>
                          </div>
                        </article>
                      )}
                    <div className={styles.pulseFooter}>
                      <button
                        className={styles.quietButton}
                        type="button"
                        onClick={() => navigate('/knowledge/podcasts')}
                      >
                        全部通识课
                        <ArrowRight size={16} strokeWidth={1.5} aria-hidden />
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function KnowledgeHomeSkeleton() {
  return (
    <div
      className={styles.homeSkeleton}
      aria-busy="true"
      aria-label="正在读取真知内容"
    >
      <div className={styles.homeStack}>
        <section className={styles.section}>
          <div className={styles.skeletonSectionHeader}>
            <span
              className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}
            />
            <span
              className={`${styles.skeletonLine} ${styles.skeletonLineMid}`}
            />
          </div>
          <div className={styles.currentListenStack}>
            <div className={styles.currentQueuePanel}>
              <div className={styles.currentQueueHeader}>
                <span
                  className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}
                />
                <span
                  className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}
                />
              </div>
              <div className={styles.queueGrid}>
                {[0, 1, 2].map((item) => (
                  <article className={styles.queueCard} key={item}>
                    <span className={styles.queueIndex}>
                      {queueLabelForIndex(item)}
                    </span>
                    <span className={styles.queueBody}>
                      <span
                        className={`${styles.skeletonLine} ${styles.skeletonLineWide}`}
                      />
                      <span
                        className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}
                      />
                    </span>
                  </article>
                ))}
              </div>
            </div>
            <article className={styles.currentLessonCard}>
              <div className={styles.skeletonStack}>
                <span
                  className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}
                />
                <span
                  className={`${styles.skeletonLine} ${styles.skeletonLineWide}`}
                />
                <span
                  className={`${styles.skeletonLine} ${styles.skeletonLineMid}`}
                />
              </div>
              <div className={styles.homeAudioPlayer}>
                <span className={styles.skeletonButton} />
                <span
                  className={`${styles.skeletonLine} ${styles.skeletonLineWide}`}
                />
                <span className={styles.skeletonButton} />
              </div>
            </article>
          </div>
        </section>

        <section
          className={`${styles.section} ${styles.knowledgeUnit} ${styles.optionalSection}`}
        >
          <div className={styles.skeletonSectionHeader}>
            <span
              className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}
            />
            <span
              className={`${styles.skeletonLine} ${styles.skeletonLineMid}`}
            />
          </div>
          <div className={styles.courseList}>
            {[0, 1, 2].map((item) => (
              <article className={styles.courseListItem} key={item}>
                <span className={styles.skeletonIcon} />
                <div className={styles.courseListBody}>
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonLineWide}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonLineMid}`}
                  />
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
